import View from "./View";
import { createSingleKeyPressBinding } from "./keyboard/bindKeyboard";
import { getNextSelectedOptionModelFromArrowPress } from "./keyboard/handleArrowPress";
import audioManager from '../_audio/audio-manager'

//returns an instance of a  menuModel.
export default function(config = {}, initialViewKey = null) {
  //Create view models with defaults
  let views = {};
  Object.keys(config.views).forEach(userViewModelKey => {
    const userViewModelOptions = config.views[userViewModelKey];
    views[userViewModelKey] = new View(userViewModelOptions);
  });

  //Get the initial snapshot's view to use. User can pass this in.
  //(EX: start me on the Journal page)
  const firstViewKey = initialViewKey || Object.keys(views)[0]; //Default to first in Object keys
  const activeViewModel = views[firstViewKey];

  //Get first option that is not INVISIBLE or DISABLED
  const firstOptionKey = Object.keys(
    activeViewModel.menuOptions
  ).filter(optionKey => {
    const model = activeViewModel.menuOptions[optionKey];
    return model.isVisible() && !model.isDisabled();
  })[0];

  const firstOptionModel = activeViewModel.menuOptions[firstOptionKey];
  const initialSelectedOptionId = firstOptionModel.id;

  return {
    menuState: {
      isLocked: false, //for disabling movement while modals are up, etc (or multiple instances)
      //Locking prevents `setActive` and `submit` from doing anything
      viewId: firstViewKey,
      activeViewModel: activeViewModel,
      selectedOptionId: initialSelectedOptionId,

      rememberingOptionIds: {
        //space to record `viewId: optionId` so we know the right one to select when going
      }
    },

    //Listening and Handling
    listenHandlers: [],
    listen(handler) {
      this.listenHandlers.push(handler);
    },

    /** Fire each handler with a new copy of our latest state */
    fireHandlers() {
      this.listenHandlers.forEach(handler => {
        if (typeof handler === "function") {
          handler({ ...this.getSnapshot() });
        }
      });
    },

    /** Sets `selectedOptionId` for visual difference on which is cursored */
    setActive(model, fromKeyboard=false) {
      //Cancel here if the menu is locked
      if (this.menuState.isLocked) {
        // console.log('NOPE. menu is locked')
        return;
      }

      //Look model up in our under-the-hood system
      const selectedOptionModel = this.getOptionModelFromId(model.id);

      if (!selectedOptionModel) {
        //A wrong thing was trying to be accessed
        console.warn("misfire!", model);
        return;
      }

      //Diff the new and old for keyboard sound
      if (this.menuState.selectedOptionId !== selectedOptionModel.id) {
        if (fromKeyboard) {
          //TODO This should probably be a setting in the loaded view? Opt in or out of cursor sounds?
          playCursorSound()
        }
      }



      //make sure it's valid to be selected: a) not already selected, and b) is not Disabled
      if (
        !selectedOptionModel.isSelected &&
        !selectedOptionModel.isDisabled()
      ) {
        //Do our change
        this.mergeChange({
          selectedOptionId: selectedOptionModel.id
        });
      }
    },

    /** Submit the thing that this Option does. EX: ENTER KEY or CLICKING on an option */
    submit(model) {
      //Cancel here if the menu is locked
      if (this.menuState.isLocked) {
        // console.log('NOPE. menu is locked')
        return;
      }

      const selectedOptionModel = this.getOptionModelFromId(model.id);

      //Cancel here if this model is not able to be submitted
      if (!selectedOptionModel.isAbleToBeSubmitted()) {
        return;
      }

      //Cancel here if this model is "unavailable", which means it can be cursored to but not submitted.
      if (selectedOptionModel.isUnavailable()) {
        return;
      }

      //Do the Enter handler (EX: data setting before transitioning to next view)
      selectedOptionModel.handleOptionEnter(this); //pass in the instance of the menu in case handler needs it

      //Check for nextView change. (many options will set data above THEN transition to a new view)
      //While some options simply set data without affecting menu state
      if (selectedOptionModel.nextView) {
        this.goToNextView(selectedOptionModel);
      }
    },

    /** Go to this option's menu area "view" */
    goToNextView(model) {
      const viewKey = model.nextView;
      const nextArea = new View(config.views[viewKey]);

      if (nextArea) {
        //Remember our current option in case we come back to this view!
        const currentView = new View(config.views[this.menuState.viewId]); //get a ref of the current View model

        //Create a flag that notes if an option is okay to be remembered or not.
        const shouldRememberOption = (
          currentView.shouldRememberOptions && //This view is allowing options to be remembered
          !currentView.dontRememberOptionIds.includes( //This option ID is not excluded from remembering
            this.menuState.selectedOptionId
          )
        );

        //Add this optionId to our remembering object (or set it to null because its blacklisted, so the remembering resets)
        const updatedRememberingState = {
          ...this.menuState.rememberingOptionIds,
          [this.menuState.viewId]: shouldRememberOption
            ? this.menuState.selectedOptionId
            : null
        };
        this.mergeChange({
          rememberingOptionIds: updatedRememberingState
        });

        //----- Find the next option to select when we get to the next view ------------------
        //let nextSelectedOptionKey = null; //We'll get a value as we go through this:

        const availableOptionIdsInNextView = Object.keys(
          nextArea.menuOptions
        ).filter(optionKey => {
          const model = nextArea.menuOptions[optionKey];
          return model.isVisible() && !model.isDisabled();
        });

        //Now, for the NEXT VIEW, see if we have a remembered one.
        const rememberedOptionIdForNextView = this.menuState
          .rememberingOptionIds[viewKey];

        //Start with the View's wanted default selection.
        //Ask for View's preferred first selected (will default to first)
        let nextSelectedOptionKey = nextArea.getFirstSelectedOptionOnActivation(
          availableOptionIdsInNextView.map(id => nextArea.menuOptions[id])
        );

        //But if we have a remembered one, override to that!
        if (rememberedOptionIdForNextView && availableOptionIdsInNextView.includes(rememberedOptionIdForNextView)) {
          nextSelectedOptionKey = rememberedOptionIdForNextView;
        }

        const nextSelectedOption = nextArea.menuOptions[nextSelectedOptionKey];
        const nextSelectedOptionId = nextSelectedOption
          ? nextArea.menuOptions[nextSelectedOptionKey].id
          : availableOptionIdsInNextView[0]; //In the event that `nextSelectedOption` is undefined (some MenuOption needs a manual id), just fall back to the first key.

        //Transition to new view with correct option selected --------
        this.mergeChange({
          viewId: viewKey,
          activeViewModel: nextArea,
          selectedOptionId: nextSelectedOptionId
        });
      } else {
        console.warn(viewKey, "view not found in `views`: ", views);
      }
    },

    /** Retrieve one option from our internal list of Option Models */
    getOptionModelFromId(id = "") {
      //Turn each key into a model, and find the one that matches passed in ID
      return Object.keys(this.menuState.activeViewModel.menuOptions)
        .map(optionKey => {
          return this.menuState.activeViewModel.menuOptions[optionKey];
        })
        .find(optionModel => {
          return id === optionModel.id;
        });
    },

    mergeChange(changes = {}) {
      this.menuState = {
        ...this.menuState,
        ...changes
      };
      this.fireHandlers();
    },

    getOptionsList() {
      return Object.keys(
        this.menuState.activeViewModel.menuOptions
      ).map(optionKey => {
        const model = this.menuState.activeViewModel.menuOptions[optionKey];
        return {
          id: model.id,
          viewBag: model.viewBag || {},
          isVisible: model.isVisible(),
          isUnavailable: model.isUnavailable(),
          isDisabled: model.isDisabled(),
          isSelected: model.id === this.menuState.selectedOptionId,
          label: model.label,
          description: model.description,
          belongsToGroup: model.optionGroup
        };
      });
    },
    getOptionsData() {
      let data = {};

      Object.keys(
        this.menuState.activeViewModel.menuOptions
      ).forEach(optionKey => {
        const model = this.menuState.activeViewModel.menuOptions[optionKey];

        data[optionKey] = {
          id: model.id,
          viewBag: model.viewBag || {},
          isVisible: model.isVisible(),
          isUnavailable: model.isUnavailable(),
          isDisabled: model.isDisabled(),
          isSelected: model.id === this.menuState.selectedOptionId,
          label: model.label,
          description: model.description
        };
      });

      return data;
    },

    getSnapshot() {
      const currentlySelectedOptionData = this.getOptionsList().find(
        o => o.isSelected
      );
      if (!currentlySelectedOptionData) {
        // console.log(this.getOptionsList());
        console.warn("no selection");
      }

      //This is the snapshot
      return {
        submit: this.submit.bind(this), //include a reference to the submitting function
        setActive: this.setActive.bind(this), //and setting active

        viewId: this.menuState.viewId,
        viewBag: this.menuState.activeViewModel.viewBag,
        optionsData: this.getOptionsData(), //key:value so the client can reference a specific option by key: EX: optionsData["character"]
        //optionsList: optionsList, //If you just want to loop through a list
        //TODO: I took out optionsList because it's super redundant of optionsData. Just generate a list from optionsData on the other side
        currentlySelectedOptionData: currentlySelectedOptionData
      };
    },

    /** Call this to unmount the bindings for clean exit */
    destroy() {
      // console.log('destroy');
      this.listenHandlers = [];

      //Remove bindings!
      let el = document.getElementsByTagName("body")[0];

      //New Way: loop through our bindings and remove the keyUp and keyDown handlers
      [
        this.bindings.submitKey,
        this.bindings.downKey,
        this.bindings.upKey,
        this.bindings.leftKey,
        this.bindings.rightKey,
        this.bindings.escKey
      ].forEach(keyBinding => {
        keyBinding.forEach(handlersObject => {
          //for each binding, get rid of the Up and Down handlers
          el.removeEventListener("keyup", handlersObject.keyUpHandler);
          el.removeEventListener("keydown", handlersObject.keyDownHandler);
        });
      });
      //I believe these are unbinding properly? but it's a little hard to test

      //OLD WAY: remove the bindings!
      // //Enter
      // el.removeEventListener("keyup", this.bindings.submitKey.keyUpHandler);
      // el.removeEventListener("keydown", this.bindings.submitKey.keyDownHandler);
      // //Down
      // el.removeEventListener("keyup", this.bindings.downKey.keyUpHandler);
      // el.removeEventListener("keydown", this.bindings.downKey.keyDownHandler);
      // //Up
      // el.removeEventListener("keyup", this.bindings.upKey.keyUpHandler);
      // el.removeEventListener("keydown", this.bindings.upKey.keyDownHandler);
      // //Left
      // el.removeEventListener("keyup", this.bindings.leftKey.keyUpHandler);
      // el.removeEventListener("keydown", this.bindings.leftKey.keyDownHandler);
      // //Right
      // el.removeEventListener("keyup", this.bindings.rightKey.keyUpHandler);
      // el.removeEventListener("keydown", this.bindings.rightKey.keyDownHandler);
    },

    lock() {
      this.mergeChange({ isLocked: true });
    },
    unlock() {
      this.mergeChange({ isLocked: false });
    },

    /** Set up and provide initial snapshot */
    init() {
      // console.log("under the hood state", this.menuState);

      //Do any bindings here...
      this.bindings = {
        submitKey: createSingleKeyPressBinding([13, 32], () => {
          //Enter, Space
          this.submit({
            id: this.menuState.selectedOptionId
          });
        }),
        escKey: createSingleKeyPressBinding([27], () => {
          const activeModel = this.menuState.activeViewModel;
          if (activeModel.backOptionId) {
            //Lookup the Back option by config'ed id
            const backOption =
              activeModel.menuOptions[activeModel.backOptionId];
            //Note, getMenuOptions() returns a different ID than straight up `menuOptions`

            //If we found one, set submit it
            if (backOption) {
              this.submit(backOption);
            } else {
              console.warn(
                "back option looked for but not found",
                activeModel.backOptionId
              );
            }
          }
          //Find back option and submit it (even if it isn't displayed in the View Layer)
        }),
        downKey: createSingleKeyPressBinding([40, 83], () => {
          //down arrow, S
          //See comment notes on RIGHT handler
          const nextSelectedOptionModel = getNextSelectedOptionModelFromArrowPress(
            "down",
            this.menuState
          );
          if (nextSelectedOptionModel) {
            this.setActive(nextSelectedOptionModel, true); //true = from keyboard flag
          }
        }),
        upKey: createSingleKeyPressBinding([38, 87], () => {
          //up arrow, W
          //See comment notes on RIGHT handler
          const nextSelectedOptionModel = getNextSelectedOptionModelFromArrowPress(
            "up",
            this.menuState
          );
          if (nextSelectedOptionModel) {
            this.setActive(nextSelectedOptionModel, true);
          }
        }),
        leftKey: createSingleKeyPressBinding([37, 65], () => {
          //left arrow, A
          //See comment notes on RIGHT handler
          const nextSelectedOptionModel = getNextSelectedOptionModelFromArrowPress(
            "left",
            this.menuState
          );
          if (nextSelectedOptionModel) {
            this.setActive(nextSelectedOptionModel, true);
          }
        }),
        rightKey: createSingleKeyPressBinding([39, 68], () => {
          //right arrow, D

          //Might need to refactor a tiny bit in the future if arrowing
          //does stuff other than changing the selected value. For now, let's just have this
          //return the next model and give it to `setActive`
          const nextSelectedOptionModel = getNextSelectedOptionModelFromArrowPress(
            "right",
            this.menuState
          );
          if (nextSelectedOptionModel) {
            this.setActive(nextSelectedOptionModel, true);
          }
        })
      };

      //Return a SNAPSHOT of whatever the menu is right now
      return this.getSnapshot();
    }
  };
}

//TODO: this should be configurable, probably. Maybe Not all menus will want to do this, or they might have their own sound?
function playCursorSound() {
  audioManager.playSfx("sfx_cursorMove")
}



// WEBPACK FOOTER //
// ./src/menu-engine/index.js